/**
 * Date Utilities
 *
 * Functions for formatting and manipulating dates.
 * These are auto-imported by Nuxt 3.
 *
 * @example
 * // In any component or composable:
 * const formatted = formatDate(new Date())
 * const days = getDaysDifference(dateA, dateB)
 */

// =============================================================================
// FORMATTERS
// =============================================================================

/**
 * Format a date to "MM-DD-Weekday" (e.g., "02-25-Sat")
 * @param {string|Date} dateInput - Date to format
 * @returns {string}
 */
export function formatFollowUpDate(dateInput) {
  const date = toDate(dateInput)
  if (!date) return ""

  const weekday = date.toLocaleDateString("en-US", { weekday: "short" })
  const month = padZero(date.getMonth() + 1)
  const day = padZero(date.getDate())

  return `${month}-${day}-${weekday}`
}

/**
 * Format a date to time only "HH:MM am/pm" (e.g., "2:30 pm")
 * @param {string|Date} dateInput - Date to format
 * @returns {string}
 */
export function formatFollowUpCreatedTime(dateInput) {
  const date = toDate(dateInput)
  if (!date) return ""

  return formatTime(date)
}

/**
 * Format a date to "MM.DD.Weekday - HH:MM am/pm" (e.g., "02.25.Sat - 2:30 pm")
 * @param {string|Date} dateInput - Date to format
 * @returns {string}
 */
export function formatFollowUpCreatedDate(dateInput) {
  const date = toDate(dateInput)
  if (!date) return ""

  const weekday = date.toLocaleDateString("en-US", { weekday: "short" })
  const month = padZero(date.getMonth() + 1)
  const day = padZero(date.getDate())
  const time = formatTime(date)

  return `${month}.${day}.${weekday} - ${time}`
}

/**
 * Format a date to relative time (e.g., "5 mins ago", "2 hours ago")
 * @param {string|Date} dateInput - Date to format
 * @returns {string}
 */
export function formatRelativeTime(dateInput) {
  const date = toDate(dateInput)
  if (!date) return ""

  const now = new Date()
  const diffMs = now - date
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return "just now"
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes === 1 ? "" : "s"} ago`
  }

  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`
  }

  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`
}

// Legacy alias
export const formatRecentFollowUpDateTime = formatRelativeTime

/**
 * Format a date to standard US format "MM/DD/YY, HH:MM AM/PM"
 * @param {string|Date} dateInput - Date to format
 * @returns {string}
 */
export function formatToStandardDate(dateInput) {
  const date = toDate(dateInput)
  if (!date) return ""

  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

/**
 * Format a date to "YYYY-MM-DD - Weekday" (e.g., "2023-02-25 - Saturday")
 * @param {string|Date} dateInput - Date to format
 * @returns {string}
 */
export function formatCaseCreatedAtDate(dateInput) {
  const date = toDate(dateInput)
  if (!date) return ""

  const year = date.getFullYear()
  const month = padZero(date.getMonth() + 1)
  const day = padZero(date.getDate())
  const weekday = date.toLocaleString("en-US", { weekday: "long" })

  return `${year}-${month}-${day} - ${weekday}`
}

// =============================================================================
// CALCULATIONS
// =============================================================================

/**
 * Get the difference in days between two dates
 * @param {string|Date} dateA - First date
 * @param {string|Date} dateB - Second date
 * @returns {number} Difference in days (dateA - dateB), positive if A is later
 * @example
 * getDaysDifference('2023-02-25', '2023-02-20') // 5
 */
export function getDaysDifference(dateA, dateB) {
  const dA = toDate(dateA)
  const dB = toDate(dateB)

  if (!dA || !dB) return 0

  // Normalize to start of day for accurate day comparison
  dA.setHours(0, 0, 0, 0)
  dB.setHours(0, 0, 0, 0)

  const diffMs = dA - dB

  return Math.round(diffMs / (1000 * 60 * 60 * 24))
}

/**
 * Get the number of days passed since a date
 * @param {string|Date} dateInput - The date to compare from
 * @returns {number} Number of days since the date
 * @example
 * getDaysPassedSince('2023-02-20') // number of days from then to now
 */
export function getDaysPassedSince(dateInput) {
  return getDaysDifference(new Date(), dateInput)
}

/**
 * Get the number of weeks passed since a date
 * @param {string|Date} dateInput - The date to compare from
 * @returns {number} Number of full weeks since the date
 */
export function getWeeksPassedSince(dateInput) {
  const days = getDaysPassedSince(dateInput)

  return Math.floor(days / 7)
}

// =============================================================================
// ISO 8601 CONVERSIONS
// =============================================================================

/**
 * Convert a date to ISO 8601 string
 * @param {string|Date} dateInput - Date to convert
 * @returns {string} ISO 8601 string or empty string if invalid
 */
export function toIso8601String(dateInput) {
  const date = toDate(dateInput)
  if (!date) return ""

  return date.toISOString()
}

/**
 * Convert an ISO 8601 string to a Date object
 * @param {string} isoString - ISO 8601 string
 * @returns {Date|null} Date object or null if invalid
 */
export function fromIso8601String(isoString) {
  if (!isoString) return null

  const date = new Date(isoString)

  return isNaN(date.getTime()) ? null : date
}

// =============================================================================
// FOLLOW-UP DATE CONVENTIONS
// -----------------------------------------------------------------------------
// `occurredAt` on a follow-up is semantically a CALENDAR DATE, not a moment.
// To keep ordering and grouping unambiguous regardless of where the user or
// server are located, we store it as noon UTC of the chosen day, and the
// client/server agree on that contract:
//   - UI inputs (flatpickr) work in `YYYY-MM-DD`.
//   - Wire format is `YYYY-MM-DDT12:00:00.000Z`.
//   - Backend `resolveInput` re-normalises any incoming timestamp.
// Use these helpers everywhere occurredAt is read/written so we don't
// re-introduce the "saved today, shows tomorrow" class of TZ bugs.
// =============================================================================

/**
 * A date's calendar day in the user's LOCAL timezone, formatted "YYYY-MM-DD".
 * Local (not UTC) on purpose: calendar grids and flatpickr render local days, so
 * the day key must match what the user sees. Used by the calendar (cell dates +
 * heatmap) and the /cases planning-date highlight.
 *
 * @param {Date} date
 * @returns {string}
 */
export function localYyyyMmDd(date) {
  const y = date.getFullYear()
  const m = padZero(date.getMonth() + 1)
  const day = padZero(date.getDate())

  return `${y}-${m}-${day}`
}

/**
 * Today's calendar date in the user's local timezone, formatted "YYYY-MM-DD".
 * Use as the default value for an occurredAt date picker on a brand new note.
 *
 * @returns {string}
 */
export function todayLocalYyyyMmDd() {
  return localYyyyMmDd(new Date())
}

/**
 * flatpickr config for a note's "Date Occurred" picker (case / staff / contact
 * modals): a calendar DATE, no time, capped at today.
 *
 * Build it FRESH each time the modal opens. The picker copies its config once
 * when it mounts, and the dialog stays mounted, so a config built at load
 * time kept yesterday's cap in a tab left open past midnight. The modals pair
 * this with a `:key` bump on open.
 *
 * The wire value stays `Y-m-d` (parsed by `yyyyMmDdToUtcNoonIso`), and the
 * field shows the friendly `Y-m-d - D` ("2026-05-20 - Wed").
 *
 * @param {Date} [now]
 * @returns {object}
 */
export function followUpDatePickerConfig(now = new Date()) {
  return {
    maxDate: now,
    dateFormat: 'Y-m-d',
    altInput: true,
    altFormat: 'Y-m-d - D',
    /* eslint-disable-next-line camelcase -- flatpickr API */
    time_24hr: false,
  }
}

/**
 * Convert a "YYYY-MM-DD" string (no time, no TZ) into the canonical wire
 * format for occurredAt: noon UTC of that calendar date.
 *
 * Returns null if the input isn't in the expected shape, so callers can
 * decide whether to drop the field or fall back to a default.
 *
 * @param {string} yyyyMmDd
 * @returns {string|null}
 */
export function yyyyMmDdToUtcNoonIso(yyyyMmDd) {
  if (!yyyyMmDd) return null
  const match = String(yyyyMmDd).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null

  return `${match[1]}-${match[2]}-${match[3]}T12:00:00.000Z`
}

/**
 * Convert a stored occurredAt timestamp (expected to be noon UTC) back to
 * a "YYYY-MM-DD" string in UTC, suitable for re-populating the date picker
 * when editing a follow-up. Reading UTC components (not local) is the
 * correct choice here because the server stores the calendar date as UTC.
 *
 * @param {string|Date|null|undefined} input
 * @returns {string}
 */
export function occurredAtToYyyyMmDd(input) {
  const date = toDate(input)
  if (!date) return ""
  const y = date.getUTCFullYear()
  const m = padZero(date.getUTCMonth() + 1)
  const day = padZero(date.getUTCDate())

  return `${y}-${m}-${day}`
}

// =============================================================================
// HELPERS (internal)
// =============================================================================

/**
 * Safely convert input to Date object
 * @param {string|Date|null|undefined} input
 * @returns {Date|null}
 */
function toDate(input) {
  if (!input) return null
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input

  const day = parseCalendarDay(input)
  if (day) return day

  const date = new Date(input)

  return isNaN(date.getTime()) ? null : date
}

/**
 * A bare calendar day ("2026-07-01", no time — e.g. `CaseStaff.startingDate`,
 * a Keystone `calendarDay`) as LOCAL midnight of that day. `new Date("2026-07-01")`
 * is UTC midnight, which in the agency's timezone is the evening BEFORE — every
 * staff start date read one day early (Sandy 2026-09-30). Anything that isn't
 * exactly `YYYY-MM-DD` returns null, so callers fall back to normal parsing.
 *
 * @param {unknown} input
 * @returns {Date|null}
 */
export function parseCalendarDay(input) {
  const m = typeof input === 'string' && input.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null

  const date = new Date(+m[1], +m[2] - 1, +m[3])

  return isNaN(date.getTime()) ? null : date
}

/**
 * Pad a number with leading zero
 * @param {number} num
 * @returns {string}
 */
function padZero(num) {
  return String(num).padStart(2, "0")
}

/**
 * Format time portion of date to "H:MM am/pm"
 * @param {Date} date
 * @returns {string}
 */
function formatTime(date) {
  const hours = date.getHours()
  const minutes = padZero(date.getMinutes())
  const ampm = hours >= 12 ? "pm" : "am"
  const hour12 = hours % 12 || 12

  return `${hour12}:${minutes} ${ampm}`
}

/**
 * A "HH:MM" clock time (24h, as the time-window fields store it) written the
 * SHORT way (Sandy 2026-08-26): "12am", "8am", "1:30am", "4:45pm".
 *
 * On the hour drops the ":00", the suffix is lowercase and joined on — these
 * sit in a table cell where a window is two of them plus a dash, and the long
 * form ("9:00 AM - 2:30 PM") spent most of its width on zeroes.
 *
 * Returns null for anything that isn't a time, so callers can treat "not set"
 * and "malformed" the same way — these fields are entirely optional.
 *
 * @param {string} hhmm
 * @returns {string|null}
 */
export function formatClockTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? '').trim())
  if (!m) return null

  const hours = Number(m[1])
  const minutes = Number(m[2])
  if (hours > 23 || minutes > 59) return null

  const suffix = hours >= 12 ? 'pm' : 'am'
  const hour12 = hours % 12 || 12

  return minutes
    ? `${hour12}:${padZero(minutes)}${suffix}`
    : `${hour12}${suffix}`
}

/**
 * A TIME WINDOW as one string — "12am-4pm", "1:30am-4:45pm".
 *
 * No spaces: the pair is one value, and in a column of them the dash reads as
 * a range rather than as two separate things.
 *
 * Either end may be missing and that's a normal state (see TimeWindowFields):
 * one-sided windows keep an arrow — "6pm→" / "→8pm" — so it's obvious which
 * end is known, rather than showing a lone time that could be either.
 *
 * Null when neither end is set — the caller decides what "no window" looks
 * like in its own context.
 *
 * @param {string} start
 * @param {string} end
 * @returns {string|null}
 */
export function formatTimeWindow(start, end) {
  const from = formatClockTime(start)
  const to = formatClockTime(end)

  if (from && to) return `${from}-${to}`
  if (from) return `${from}→`
  if (to) return `→${to}`

  return null
}
